import React, { useEffect, useState, useCallback } from 'react';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  Button,
  Alert,
  CircularProgress,
} from '@mui/material';
import { getProjectsDynamicSummary, updateFinancePackage } from '../api';

interface ProjectRow {
  id: number;
  external_project_id: number | null;
  name: string;
  is_active: boolean;
  total_quota: number;
  total_paid: number;
  avg_unit_price: number;
  active_package_id: number | null;
  paid_balance: number;
  leads_sent: number;
  leads_approved: number;
  leads_total: number;
}

const currency = new Intl.NumberFormat('ru-RU', { style: 'currency', currency: 'RUB', maximumFractionDigits: 0 });

export const ProjectsDynamicTablePage: React.FC = () => {
  const [rows, setRows] = useState<ProjectRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [edits, setEdits] = useState<Record<number, { unitPrice?: string; leadQuota?: string }>>({});
  const [savingId, setSavingId] = useState<number | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await getProjectsDynamicSummary();
      setRows(data);
    } catch (err: any) {
      setError(err.message || 'Ошибка загрузки');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const setEdit = (id: number, field: 'unitPrice' | 'leadQuota', value: string) => {
    setEdits(prev => ({ ...prev, [id]: { ...prev[id], [field]: value } }));
  };

  const save = async (row: ProjectRow) => {
    if (!row.active_package_id) {
      setError(`У проекта "${row.name}" нет активного пакета. Создайте пакет на странице Финансы.`);
      return;
    }
    const changes = edits[row.id] || {};
    const payload: any = {};
    if (changes.unitPrice !== undefined && changes.unitPrice !== '') {
      const v = parseFloat(changes.unitPrice);
      if (!Number.isFinite(v) || v < 0) {
        setError('Неверная цена за лид');
        return;
      }
      payload.unitPrice = v;
    }
    if (changes.leadQuota !== undefined && changes.leadQuota !== '') {
      const v = parseInt(changes.leadQuota, 10);
      if (!Number.isFinite(v) || v < 0) {
        setError('Неверный лимит');
        return;
      }
      payload.leadQuota = v;
    }
    if (Object.keys(payload).length === 0) return;

    setSavingId(row.id);
    setError(null);
    try {
      await updateFinancePackage(row.active_package_id, payload);
      setEdits(prev => { const copy = { ...prev }; delete copy[row.id]; return copy; });
      await load();
    } catch (err: any) {
      setError(err.message || 'Ошибка сохранения');
    } finally {
      setSavingId(null);
    }
  };

  if (loading && rows.length === 0) return <CircularProgress />;

  return (
    <Box>
      <Typography variant="h5" gutterBottom>Сводка по проектам</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Баланс пакета, отправлено и утверждено лидов. Inline-редактирование цены за лид и лимита пакета.
      </Typography>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Проект</TableCell>
              <TableCell align="right">Баланс</TableCell>
              <TableCell align="right">Цена за лид</TableCell>
              <TableCell align="right">Лимит пакета</TableCell>
              <TableCell align="right">Отправлено</TableCell>
              <TableCell align="right">Утверждено</TableCell>
              <TableCell align="right">Конверсия</TableCell>
              <TableCell />
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map(row => {
              const edit = edits[row.id] || {};
              const conv = row.leads_sent > 0 ? Math.round((row.leads_approved / row.leads_sent) * 1000) / 10 : 0;
              const hasEdits = edit.unitPrice !== undefined || edit.leadQuota !== undefined;
              return (
                <TableRow key={row.id} hover>
                  <TableCell>
                    <Typography variant="body2" fontWeight={600}>{row.name}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      ext: {row.external_project_id ?? '—'}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">{currency.format(row.paid_balance)}</TableCell>
                  <TableCell align="right">
                    <TextField
                      size="small"
                      type="number"
                      value={edit.unitPrice ?? row.avg_unit_price.toFixed(2)}
                      onChange={e => setEdit(row.id, 'unitPrice', e.target.value)}
                      inputProps={{ min: 0, step: 0.01, style: { textAlign: 'right' } }}
                      sx={{ width: 100 }}
                      disabled={!row.active_package_id}
                    />
                  </TableCell>
                  <TableCell align="right">
                    <TextField
                      size="small"
                      type="number"
                      value={edit.leadQuota ?? row.total_quota}
                      onChange={e => setEdit(row.id, 'leadQuota', e.target.value)}
                      inputProps={{ min: 0, step: 1, style: { textAlign: 'right' } }}
                      sx={{ width: 90 }}
                      disabled={!row.active_package_id}
                    />
                  </TableCell>
                  <TableCell align="right">{row.leads_sent}</TableCell>
                  <TableCell align="right">{row.leads_approved}</TableCell>
                  <TableCell align="right">{conv}%</TableCell>
                  <TableCell>
                    {hasEdits && (
                      <Button
                        size="small"
                        variant="contained"
                        onClick={() => save(row)}
                        disabled={savingId === row.id}
                      >
                        {savingId === row.id ? '…' : 'Сохранить'}
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              );
            })}
            {rows.length === 0 && !loading && (
              <TableRow>
                <TableCell colSpan={8} align="center">
                  <Typography variant="body2" color="text.secondary">Нет активных проектов</Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};
